import {
  PersonalizedProductDetailsResponse,
  ProductPriceObj,
} from 'api/product/getPersonalizedProductDetails/types';
import cn from 'classnames';
import LazyMotionWrapper from 'components/lazy-motion-wrapper';
import Sheet from 'components/sheet';
import { OrderType } from 'constants/order';
import useMobile from 'hooks/common/use-mobile';
import { StaticPDPProduct, Variant } from 'modules/product-details/types';
import { AnimatePresence, m } from 'motion/react';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Gtm, gtm } from 'tracking/gtm';
import VariantDisplay from './variant-display';

interface Props {
  couponPrices?: ProductPriceObj;
  orderType: OrderType;
  pricePerKilo?: number;
  product: StaticPDPProduct;
  selectedVariant?: Variant;
  selectedVariantDiscount: PersonalizedProductDetailsResponse['variants'][number];
  onVariantIdChange: (variantId: string | null) => void;
}

/**
 * Variant selector component that displays the available variants of a product.
 */
const VariantSelectorTest: React.FC<Props> = ({
  couponPrices,
  orderType,
  pricePerKilo,
  product,
  selectedVariant,
  selectedVariantDiscount,
  onVariantIdChange,
}) => {
  const isMobile = useMobile();
  const [isOpen, setIsOpen] = useState(false);

  const onVariantChange = useCallback(
    (variantId: string) => {
      if (selectedVariant?.id === variantId) return;
      onVariantIdChange(variantId);

      gtm({
        group: Gtm.GroupName.Product,
        name: Gtm.ProductEventName.SwitchVariant,
        data: {
          product: {
            productTitle: product.title,
            productId: product.id + '',
            productType: product.productType,
            tags: product.tags.join(','),
            variantId: variantId + '',
          },
        },
      });
    },
    [
      product.id,
      product.productType,
      product.tags,
      product.title,
      selectedVariant?.id,
      onVariantIdChange,
    ]
  );

  const content = (
    <div className={cn('flex flex-col gap-2', isMobile ? 'mt-6' : 'py-2')}>
      {product.variants.map((variant) => {
        // The following logic is kind of duplicated from the state.
        // In case this test variant is going to be implemented in the future, this logic should be moved to the state
        // in a better way.
        const _selectedVariantDiscount = couponPrices?.variants?.find(
          (v) => v.id === variant.id + ''
        );

        const subscriptionPrice = Number(
          _selectedVariantDiscount?.subscription?.compare.after
        );

        const price =
          orderType === OrderType.SUBSCRIPTION
            ? subscriptionPrice
            : Number(_selectedVariantDiscount?.one_off.price);

        const productWeight =
          variant &&
          (variant.weightUnit === 'GRAMS'
            ? variant.weight / 1000
            : variant.weight);
        const pricePerKilo = price / (productWeight || 1);

        return (
          <VariantDisplay
            couponPrices={couponPrices}
            imageSize={isMobile ? 56 : 38}
            key={variant.id}
            orderType={orderType}
            product={product}
            variant={variant}
            isList={!isMobile}
            pricePerKilo={pricePerKilo}
            selectedVariantDiscount={_selectedVariantDiscount}
            onClick={(event) => {
              event.preventDefault();
              onVariantChange(variant.id);
              setIsOpen(false);
            }}
          />
        );
      })}
    </div>
  );

  if (!selectedVariant) return null;

  return (
    <div className="relative">
      <label
        className="text-text-secondary mb-1 inline-block text-sm"
        htmlFor="size"
      >
        <FormattedMessage id="pdp:buy-panel:size" />
      </label>
      <VariantDisplay
        couponPrices={couponPrices}
        hasExpandMore
        id="size"
        imageSize={isMobile ? 34 : 38}
        isOpen={isOpen}
        isList={false}
        orderType={orderType}
        pricePerKilo={product.variants.length === 1 ? pricePerKilo : undefined}
        product={product}
        selectedVariantDiscount={selectedVariantDiscount}
        variant={selectedVariant}
        onClick={(event) => {
          event.preventDefault();
          if (product.variants.length === 1) return;
          setIsOpen(!isOpen);
        }}
      />

      <Sheet isOpen={isMobile && isOpen} onClose={() => setIsOpen(false)}>
        <div className="px-4 pb-8">
          <span className="font-black">
            <FormattedMessage id="pdp:buy-panel:size" />
          </span>
          {content}
        </div>
      </Sheet>

      <LazyMotionWrapper>
        <AnimatePresence>
          {!isMobile && isOpen && (
            <m.div
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { duration: 0.2, delay: 0.1 },
                },
              }}
              className="border-grey-200 bg-other-white absolute top-25 z-50 mt-1 w-11/12 rounded-lg border shadow-sm"
            >
              {content}
            </m.div>
          )}
        </AnimatePresence>
      </LazyMotionWrapper>
    </div>
  );
};

export default VariantSelectorTest;
